import { i18nGet } from '@/src/tools/tools';

// 导出 ImportStockItem 使外部调用方可以继续使用
export interface ImportStockItem {
  sku: string;
  quantity: number;
  notes: string;
}

export interface ExcelParseResult {
  items: ImportStockItem[];
  hasError: boolean;
  errorMessage?: string;
  exceededLimit?: boolean;
}

interface WorkerResponse {
  result: ExcelParseResult;
}

// XLSX CDN路径
const XLSX_CDN_URL =
  'https://resource2.unifyxp.com/script/xlsx-0.18.5.full.min.js';

// 在全局Window上定义XLSX属性
declare global {
  interface Window {
    XLSX: any;
  }
}

// 异步加载XLSX库
const loadXLSXLib = () => {
  return new Promise((resolve, reject) => {
    // 如果已经加载，直接返回
    if (window.XLSX) {
      resolve(window.XLSX);
      return;
    }

    const script = document.createElement('script');
    script.src = XLSX_CDN_URL;
    script.async = true;
    script.onload = () => resolve(window.XLSX);
    script.onerror = () => reject(new Error('Failed to load XLSX library'));
    document.head.appendChild(script);
  });
};

/**
 * 核心Excel解析函数 - 提取为共享逻辑
 * 在Worker和主线程中复用相同的解析逻辑
 */
export function parseExcelData(
  data: ArrayBuffer,
  XLSX: any,
  invalidSkuErrorMsg: string,
  exceedLimitErrorMsg: string,
  fileUploadFailedErrorMsg: string,
): ExcelParseResult {
  try {
    const workbook = XLSX.read(data, { type: 'array' });
    const firstSheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[firstSheetName];

    // 将Excel转换为JSON对象
    const rawData = XLSX.utils.sheet_to_json(worksheet);

    // 检查文件是否为空
    if (!rawData || rawData.length === 0) {
      return {
        items: [],
        hasError: true,
        errorMessage: invalidSkuErrorMsg,
      };
    }

    // 检查行数限制
    if (rawData.length > 499) {
      return {
        items: [],
        hasError: true,
        errorMessage: exceedLimitErrorMsg,
        exceededLimit: true,
      };
    }

    // const keys = [worksheet.A1.v, worksheet.B1.v, worksheet.C1.v];
    const quantityKey = worksheet.B1.v;
    const noteKey = worksheet.C1.v;
    const skuKey = worksheet.A1.v;

    // 提取SKU、Quantity、Notes
    const items: ImportStockItem[] = rawData.map((row: any) => {
      const sku = row[skuKey] || row['SKU'] || row['sku'] || '';
      let quantity = parseFloat(
        row[quantityKey] || row['QTY'] || row['quantity'] || row['qty'] || 0,
      );
      const notes =
        row[noteKey] || row['notes'] || row['Note'] || row['note'] || '';

      // 负数或小数处理为0
      if (
        isNaN(quantity) ||
        String(quantity).indexOf('.') !== -1 ||
        quantity < 0
      ) {
        quantity = 0;
      }

      return {
        sku: sku.toString().trim(),
        quantity: Math.floor(quantity), // 去除小数部分
        notes: notes.toString().substring(0, 70), // 截断至70字符
      };
    });

    // 过滤掉没有SKU的项
    const validItems = items.filter((item) => item.sku !== '');

    // 合并相同SKU的项
    const mergedItemsArray = Object.values(
      validItems.reduce<Record<string, ImportStockItem>>((acc, item) => {
        if (acc[item.sku]) {
          acc[item.sku].quantity += item.quantity;
          acc[item.sku].notes += `+${item.notes}`;
        } else {
          acc[item.sku] = item;
        }
        return acc;
      }, {}),
    );

    // notes换行符替换成空格
    const mergedItemsArrayWithNotes = mergedItemsArray.map((item) => ({
      ...item,
      notes: item.notes.replace(/[\n\r]/g, ' '),
    }));

    // 检查是否有有效的SKU
    if (mergedItemsArrayWithNotes.length === 0) {
      return {
        items: [],
        hasError: true,
        errorMessage: invalidSkuErrorMsg,
      };
    }

    return {
      items: mergedItemsArrayWithNotes,
      hasError: false,
    };
  } catch (error) {
    return {
      items: [],
      hasError: true,
      errorMessage: fileUploadFailedErrorMsg,
    };
  }
}

/**
 * 检查文件是否为有效的Excel文件
 * @param file 文件对象
 * @returns 布尔值，指示文件是否为有效的Excel文件
 */
function isValidExcelFile(file: File): boolean {
  const validExtensions = ['.xls', '.xlsx'];
  const fileName = file.name.toLowerCase();
  return validExtensions.some((ext) => fileName.endsWith(ext));
}

/**
 * 读取文件并转换为ArrayBuffer
 */
const readFileAsArrayBuffer = (file: File): Promise<ArrayBuffer> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target?.result as ArrayBuffer);
    reader.onerror = () => reject(new Error('Failed to read the file.'));
    reader.readAsArrayBuffer(file);
  });
};

// 生成Worker代码字符串，注入核心解析函数
const generateWorkerCode = (errorMessages: any) => {
  const {
    i18nExceedLimitationError,
    i18nFileUploadFailedError,
    i18nInvalidSkuError,
    i18nInvalidFileFormatError,
  } = errorMessages;
  // 注意：函数字符串化会失去闭包环境，需要直接注入i18n变量值
  return `
    // 为确保函数名一致性，显式定义全局变量
    const i18nExceedLimitationError = '${i18nExceedLimitationError}';
    const i18nFileUploadFailedError = '${i18nFileUploadFailedError}';
    const i18nInvalidSkuError = '${i18nInvalidSkuError}';
    const i18nInvalidFileFormatError = '${i18nInvalidFileFormatError}';
    
    // 将函数定义为全局变量，使用固定名称
    const parseExcelDataFunction = ${parseExcelData.toString()};
    
    self.onmessage = function (event) {
      try {
        // 动态加载 XLSX
        importScripts('${XLSX_CDN_URL}');

        const data = event.data;
        // 使用定义的全局变量名调用函数
        const result = parseExcelDataFunction(
          data, 
          XLSX, 
          i18nInvalidSkuError,
          i18nExceedLimitationError,
          i18nFileUploadFailedError
        );
        self.postMessage({ result: result });
      } catch (error) {
        console.error('Worker error:', error);
        self.postMessage({
          result: {
            items: [],
            hasError: true,
            errorMessage: 'Worker error: ' + (error.message || 'Unknown error')
          },
        });
      }
    };
  `;
};

// 全局 worker 实例
let worker = null;
// Blob URL 引用，用于清理
let blobURL = null;

/**
 * 创建一个新的 Worker 实例
 * @returns Worker 实例
 */
const createWorker = (errorMessages: any): Worker => {
  const code = generateWorkerCode(errorMessages);

  // 创建 Blob
  const blob = new Blob([code], {
    type: 'application/javascript',
  });

  // 创建 URL
  blobURL = URL.createObjectURL(blob);

  // 创建 Worker
  return new Worker(blobURL);
};

/**
 * 使用Web Worker异步解析Excel文件
 * @param file Excel文件
 * @param i18n 国际化对象
 * @returns Promise<ExcelParseResult> 解析结果
 */
const parseExcelFile = (file: File, i18n: any): Promise<ExcelParseResult> => {
  const getI18n = (str: string) => i18nGet(i18n, str);
  const i18nExceedLimitationError = getI18n(
    'stockIn.stock.exceeding_limitation',
  );
  const i18nFileUploadFailedError = getI18n('stockIn.stock.file_upload_failed');
  const i18nInvalidSkuError = getI18n(
    'stockIn.stock.judge_file_content_failed',
  );
  const i18nInvalidFileFormatError = getI18n(
    'stockIn.stock.file_formattion_error',
  );

  return new Promise((resolve) => {
    // 检查文件格式是否为.xls或.xlsx
    if (!isValidExcelFile(file)) {
      resolve({
        items: [],
        hasError: true,
        errorMessage: i18nInvalidFileFormatError,
      });
      return;
    }

    // 主线程处理逻辑，用作Worker失败后的兜底机制
    const processInMainThread = async () => {
      console.log('使用主线程处理Excel文件');

      try {
        // 确保XLSX库已加载
        const xlsxLib = await loadXLSXLib();

        // 读取文件
        const data = await readFileAsArrayBuffer(file);

        try {
          // 使用全局XLSX变量
          window.XLSX = xlsxLib;
          // 使用共享的核心解析函数处理数据
          const result = parseExcelData(
            data,
            window.XLSX,
            i18nInvalidSkuError,
            i18nExceedLimitationError,
            i18nFileUploadFailedError,
          );
          resolve(result);
        } catch (err) {
          // 错误处理...
        }
      } catch (error) {
        // 加载XLSX或读取文件失败...
      }
    };

    try {
      // 创建 Worker 实例
      worker = createWorker({
        i18nExceedLimitationError,
        i18nFileUploadFailedError,
        i18nInvalidSkuError,
        i18nInvalidFileFormatError,
      });

      // 监听worker的消息
      worker.onmessage = (event: MessageEvent) => {
        const response = event.data as WorkerResponse;

        // 如果Worker返回了错误结果，转为主线程处理
        if (
          response.result.hasError &&
          response.result.errorMessage?.startsWith('Worker error:')
        ) {
          console.warn(
            'Worker返回错误，切换到主线程处理:',
            response.result.errorMessage,
          );
          cleanupWorker();
          processInMainThread();
        } else {
          // 正常结果，完成后终止worker
          cleanupWorker();
          resolve(response.result);
        }
      };

      // 监听worker错误
      worker.onerror = (error) => {
        console.error('Worker error:', error);
        cleanupWorker();
        // 直接切换到主线程处理
        processInMainThread();
      };

      // 读取文件并发送到worker
      readFileAsArrayBuffer(file)
        .then((data) => {
          // 确保worker仍然存在
          if (worker) {
            // 发送数据给worker处理
            worker.postMessage(data);
          } else {
            processInMainThread();
          }
        })
        .catch(() => {
          cleanupWorker();
          resolve({
            items: [],
            hasError: true,
            errorMessage: i18nInvalidSkuError,
          });
        });
    } catch (error) {
      console.error('Worker initialization error:', error);
      // 处理浏览器不支持Web Worker的情况或初始化错误
      cleanupWorker();
      // Fallback: 在主线程使用相同的解析逻辑
      processInMainThread();
    }
  });
};

/**
 * 清理 Worker 资源
 */
const cleanupWorker = () => {
  if (worker) {
    worker.terminate();
    worker = null;
  }

  // 清理 Blob URL
  if (blobURL) {
    URL.revokeObjectURL(blobURL);
    blobURL = null;
  }
};

// 手动停止worker运行
const terminateCurrentWorker = () => {
  cleanupWorker();
};

export { parseExcelFile, terminateCurrentWorker };
